import {Component, HostListener, Input, OnInit} from '@angular/core';
import {WhiteboardService} from '@app/modules/whiteboard/services/whiteboard.service';
import {Whiteboard, WhiteboardState} from '@app/state/whiteboard.state';
import {Store} from '@ngxs/store';
import {LocalUser, LocalUserState} from '@app/state/local-user.state';
import {ClassroomState, ClassroomStateModel} from '@app/state/classroom-state.service';
import {Key, KeyCode, ViewportTab} from '@app/modules/shared/data/constants';
import {Container} from '@app/modules/svg/Container';
import {Style} from '@app/modules/svg/service/style/Style';
import {Tools} from '@app/modules/svg/dataSource/Tools';
import {Tool} from '@app/modules/svg/service/tool/Tool';
import {SVGEvent} from '@app/modules/svg/dataSource/constant/SVGEvent';
import {DrawTextBox} from '@app/modules/svg/service/tool/draw/element/foreign/DrawTextBox';
import {Drawer} from '@app/modules/svg/service/tool/draw/Drawer';
import {FreeDrawer} from '@app/modules/svg/service/tool/draw/mode/FreeDrawer';
import {SelectTool} from '@app/modules/svg/service/tool/select/SelectTool';
import {DrawTool} from '@app/modules/svg/service/tool/draw/DrawTool';
import {PointerTool} from '@app/modules/svg/service/tool/pointer/PointerTool';
import {EraserTool} from '@app/modules/svg/service/tool/eraser/EraserTool';
import {ElementView} from '@app/modules/svg/element/ElementView';
import {ElementType} from '@app/modules/svg/dataSource/constant/ElementType';

@Component({
  selector: 'app-student-toolbar',
  templateUrl: './student-toolbar.component.html',
  styleUrls: ['./student-toolbar.component.css']
})
export class StudentToolbarComponent implements OnInit {

  @Input()
  public whiteboard: Whiteboard;
  @Input()
  public enableGlobalListeners: boolean;
  public localUser: LocalUser;
  public classroom: ClassroomStateModel;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public ViewportTab = ViewportTab;

  private turningOnTool: boolean;

  @Input()
  public whiteboardService: WhiteboardService;

  public activeDrawer: {linear: Drawer; shape: Drawer} = { linear: undefined, shape: undefined };
  public lastActive: {tool: Tool; drawer: Drawer} = { tool: undefined, drawer: undefined };

  constructor(private store: Store) {
    this.listenStoreChanges();
  }

  public ngOnInit(): void {
    this.init();
  }
  @HostListener('window:keydown', ['$event'])
  public keyDown(event: KeyboardEvent): void {
    if (!this.enableGlobalListeners) {
      return;
    }

    if (event.code === KeyCode.ARR_LEFT && this.wbTools.selectTool.isOn()) {
      this.wbContainer.focused.drag({x: -5, y: 0});
      if (this.wbTools.selectTool.isOn()) {
        event.preventDefault();
      } /* disable window scrolling */
    }
    if (event.code === KeyCode.ARR_RIGHT && this.wbTools.selectTool.isOn()) {
      this.wbContainer.focused.drag({x: +5, y: 0});
      if (this.wbTools.selectTool.isOn()) {
        event.preventDefault();
      } /* disable window scrolling */
    }
    if (event.code === KeyCode.ARR_DOWN && this.wbTools.selectTool.isOn()) {
      this.wbContainer.focused.drag({x: 0, y: +5});
      if (this.wbTools.selectTool.isOn()) {
        event.preventDefault();
      } /* disable window scrolling */
    }
    if (event.code === KeyCode.ARR_UP && this.wbTools.selectTool.isOn()) {
      this.wbContainer.focused.drag({x: 0, y: -5});
      if (this.wbTools.selectTool.isOn()) {
        event.preventDefault();
      } /* disable window scrolling */
    }

    if (event.shiftKey) {
      this.wbContainer.perfect = true;
    }
    if (event.code === KeyCode.ESC) {
      if (this.wbTools.drawTool.isOn()) {
        this.wbTools.drawTool.stopDrawing();
      } else if (this.wbTools.selectTool.isOn()) {
        this.wbContainer.blur();
      }
    }
    if (event.ctrlKey) {
      this.wbContainer.multiSelect();
      this.wbContainer.multiSelect();
    }
  }

  @HostListener('window:keyup', ['$event'])
  public keyUp(event: KeyboardEvent): void {
    if (!this.enableGlobalListeners) {
      return;
    }

    if (event.key === Key.SHIFT) {
      this.wbContainer.perfect = false;
    }
    if (event.code === KeyCode.DELETE) {
      const active = document.activeElement;
      if (active instanceof HTMLInputElement && (active.type === 'text' || active.type === 'number')) {return;}
      this.deleteSelected();
    }
    if (event.key === Key.CTRL) {
      this.wbContainer.singleSelect();
    }
  }

  private listenStoreChanges(): void {
    this.store.select(LocalUserState).subscribe((localUser: LocalUser) => {
      this.localUser = localUser;
    });
    this.store.select(ClassroomState).subscribe((classroom: ClassroomStateModel) => {
      this.classroom = classroom;
    });
  }

  public init(): void {
    this.wbStyle.strokeColor = this.localUser.personalColor;
    this.wbStyle.fontColor = this.localUser.personalColor;

    this.wbContainer.addCallBack(SVGEvent.DRAWER_CHANGED, this.drawerChangedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.TOOL_ON, this.toolOnCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.TOOL_OFF, this.toolOffCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.POINTER_TOOl_ON, this.pointerToolOnCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.POINTER_TOOl_OFF, this.pointerToolOffCallBack.bind(this));

    /* translation callbacks */
    this.wbContainer.addCallBack(SVGEvent.DRAW_MOUSE_DOWN, this.drawClickCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.DRAW_MOUSE_MOVE, this.drawMoveCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.DRAW_MOUSE_UP, this.drawEndCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.END_DRAWING, this.endDrawingCallBack.bind(this));

    this.wbContainer.addCallBack(SVGEvent.DRAG_MOUSE_DOWN, this.dragStartCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.DRAG_MOUSE_MOVE, this.dragMoveCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.DRAG_MOUSE_UP, this.dragEndCallBack.bind(this));

    this.wbContainer.addCallBack(SVGEvent.ROTATE_MOUSE_DOWN, this.rotateStartCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ROTATE_MOUSE_MOVE, this.rotateCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ROTATE_MOUSE_UP, this.rotateEndCallBack.bind(this));

    this.wbContainer.addCallBack(SVGEvent.RESIZE_MOUSE_DOWN, this.resizeStartCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.RESIZE_MOUSE_MOVE, this.resizeCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.RESIZE_MOUSE_UP, this.resizeEndCallBack.bind(this));
    /* translation callbacks */


    this.wbContainer.addCallBack(SVGEvent.POINTER_MOVE, this.pointerMoveCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ELEMENTS_FOCUSED, this.focusChangedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ELEMENTS_BLURRED, this.focusChangedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ALL_FOCUSED, this.focusChangedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ALL_BLURRED, this.focusChangedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.CLICKED_ON_CONTAINER, this.clickedOnContainerCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ELEMENT_CREATED, this.elementCreatedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ELEMENTS_DRAGGED, this.elementsDraggedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ELEMENTS_ROTATED, this.elementsRotatedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ELEMENTS_RESIZED, this.elementsResizedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.REF_POINT_CHANGED, this.refPointChangedCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.ELEMENTS_DELETED, this.elementsDeletedCallBack.bind(this));

    this.wbContainer.addCallBack(SVGEvent.TEXT_TYPING, this.textTypingCallBack.bind(this));
    this.wbContainer.addCallBack(SVGEvent.TEXT_TYPING_COMMIT, this.textTypingCommitCallBack.bind(this));

    this.wbContainer.tools.pointerTool.off(false);
    this.wbContainer.tools.pointerTool = this.whiteboardService.createStudentPointer(this.localUser.personalColor);

    this.whiteboardService.afterShare = () => {
      this.free();
    };
    this.whiteboardService.afterShareWith = (userId: number) => {
      if (this.localUser.id === userId) {
        if (this.whiteboard.sharedUsers?.size === 1) {
          this.pointer();
        } else {
          this.free();
        }
      }
    };
    this.whiteboardService.afterUnshare = () => {
      this.wbContainer.blur();
      this.wbActiveTool?.off();
    };
    this.whiteboardService.afterUnshareWith = (userId: number) => {
      if (this.localUser.id === userId) {
        this.wbContainer.blur();
        this.wbActiveTool?.off();
      }
    };
  }

  public select(): void {
    this.activateTool('Select', () => this.wbTools.selectTool.on());
  }
  public eraser(): void {
    this.activateTool('Eraser', () => this.wbTools.eraserTool.on());
  }
  public pointer(): void {
    this.activateTool('Pointer', () => this.wbContainer.tools.pointerTool.on());
    WhiteboardService.hidePointer(this.wbContainer.tools.pointerTool);
  }
  public free(): void {
    this.activateTool('Pen', () => {
      this.wbContainer.tools.drawTool.setDrawer(this.wbContainer.drawers.free);
      this.wbContainer.tools.drawTool.on();
    });
  }
  public textBox(): void {
    this.activateTool('Text Box', () => {
      this.wbContainer.tools.drawTool.setDrawer(this.wbContainer.drawers.textBox);
      this.wbContainer.tools.drawTool.on();
    });
  }

  public deleteSelected(): void {
    if (this.wbContainer && this.wbContainer.tools.selectTool.isOn()) {
      this.wbContainer.focused.remove();
    }
  }

  public drawerChangedCallBack(parameters): void {
    this.lastActive.drawer = parameters.oldDrawer;
  }
  public toolOnCallBack(parameters): void {

  }
  public toolOffCallBack(parameters): void {
    this.lastActive.tool = parameters.tool;
  }
  public pointerToolOnCallBack(): void {
    this.whiteboardService.sendPointerToolOn();
  }
  public pointerToolOffCallBack(): void {
    this.whiteboardService.sendPointerToolOff();
  }

  public pointerMoveCallBack(parameters): void {
    this.whiteboardService.sendPointerMouseMove(parameters);
  }
  public focusChangedCallBack(): void {
    this.whiteboardService.sendElementsFocused({elements: this.wbContainer.focused.children});
    this.recenterRefPoint(false);
  }
  public clickedOnContainerCallBack(): void {
    if (
      this.wbTools.editNodeTool.isOn() ||
      this.wbTools.editTableTool.isOn() ||
      (
        /* There is no edit text tool. Text editing functionality is written in text box drawer. */
        /* Text drawer is in editing mode, if it is turned on, and isInEditMode is true */

        /* Text box tool is on */
        this.wbTools.drawTool.isOn() && this.wbTools.drawTool.getDrawer() instanceof DrawTextBox &&
        /* Text box tool is in edit mode */
        (this.wbTools.drawTool.getDrawer() as DrawTextBox).isInEditMode
      )
    ) {
      this.free();
    }
  }
  public drawClickCallBack(parameters): void {
    this.whiteboardService.sendDrawMouseDown(parameters);
  }
  public drawMoveCallBack(parameters): void {
    this.whiteboardService.sendDrawMouseMove(parameters);
  }
  public drawEndCallBack(parameters): void {
    this.whiteboardService.sendDrawMouseUp(parameters);
  }
  public endDrawingCallBack(parameters): void {
    if (!(parameters.drawer instanceof DrawTextBox || parameters.drawer instanceof FreeDrawer) && !this.turningOnTool) {
      this.free();
    }
  }
  public elementCreatedCallBack(parameters): void {
    const elementView: ElementView = parameters.element as ElementView;

    if (this.whiteboard.streaming || elementView.type !== ElementType.TEXT_BOX) {
      this.whiteboardService.sendElementsCreated(parameters);
    }
  }

  public dragStartCallBack(parameters): void {
    this.whiteboardService.sendDragMouseDown(parameters);
  }
  public dragMoveCallBack(parameters): void {
    this.whiteboardService.sendDragMouseMove(parameters);
  }
  public dragEndCallBack(parameters): void {
    this.whiteboardService.sendDragMouseUp(parameters);
  }
  public elementsDraggedCallBack(parameters): void {
    this.whiteboardService.sendElementsDragged(parameters);
  }

  public rotateStartCallBack(parameters): void {
    this.whiteboardService.sendRotatePointMouseDown(parameters);
  }
  public rotateCallBack(parameters): void {
    this.whiteboardService.sendRotatePointMouseMove(parameters);
  }
  public rotateEndCallBack(parameters): void {
    this.whiteboardService.sendRotatePointMouseUp(parameters);
  }
  public elementsRotatedCallBack(parameters): void {
    this.whiteboardService.sendElementsRotated(parameters);
  }

  public resizeStartCallBack(parameters): void {
    this.whiteboardService.sendResizeMouseDown(parameters);
  }
  public resizeCallBack(parameters): void {
    this.whiteboardService.sendResizeMouseMove(parameters);
  }
  public resizeEndCallBack(parameters): void {
    this.whiteboardService.sendResizeMouseUp(parameters);
  }
  public elementsResizedCallBack(parameters): void {
    this.whiteboardService.sendElementsResized(parameters);
  }

  public textTypingCallBack(parameters): void {
    this.whiteboardService.sendTextTyping(parameters);
  }
  public textTypingCommitCallBack(parameters): void {
    if (parameters.oldText === '') {
      this.whiteboardService.sendElementsCreated({
        element: parameters.element
      });
    } else {
      this.whiteboardService.sendTextTypingCommit(parameters);
    }
  }

  public refPointChangedCallBack(parameters): void {
    this.whiteboardService.sendRefPointChanged(parameters);
  }
  public elementsDeletedCallBack(parameters): void {
    this.whiteboardService.sendElementsDeleted(parameters);
  }

  public recenterRefPoint(call: boolean = true): void {
    this.wbContainer?.focused.recenterRefPoint(call);
  }

  public get wbContainer(): Container | undefined {
    return this.whiteboard?.container;
  }
  public get wbStyle(): Style | undefined {
    return this.whiteboard?.container?.style;
  }
  public get wbTools(): Tools | undefined {
    return this.whiteboard?.container?.tools;
  }
  public get wbActiveTool(): Tool | undefined {
    return this.whiteboard?.container?.tools?.activeTool;
  }

  public get isSelectToolOn(): boolean {
    return this.wbActiveTool instanceof SelectTool;
  }
  public get isEraserToolOn(): boolean {
    return this.wbActiveTool instanceof EraserTool;
  }
  public get isFreeToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof FreeDrawer;
  }
  public get isTextBoxToolOn(): boolean {
    return this.wbActiveTool instanceof DrawTool && this.wbActiveTool.getDrawer() instanceof DrawTextBox;
  }
  public get isPointerToolOn(): boolean {
    return this.wbActiveTool instanceof PointerTool;
  }

  private activateTool(toolName: string, callBack: () => void): void {
    if (this.whiteboard) {
      this.turningOnTool = true;
      callBack();
      this.turningOnTool = false;
      this.log(toolName);
    }
  }

  private log(message): void {
    // this.whiteboardService.logEvent(message);
  }

  protected readonly SelectTool = SelectTool;
}
